import { useState } from 'react';
import { Radio, Terminal, Radar, LayoutGrid } from 'lucide-react';
import { MapView } from '../components/map/MapView';
import { IntersectionLayers } from '../components/map/IntersectionLayers';
import { IntersectionMarkers } from '../components/map/IntersectionMarkers';
import { DetectorMarker } from '../components/map/DetectorMarker';
import { CabinetMarker } from '../components/map/CabinetMarker';
import { FloatingPanel } from '../components/map/FloatingPanel';
import { PhaseTimeline } from '../components/map/PhaseTimeline';
import { MapGroupsDiagram } from '../components/map/MapGroupsDiagram';
import { RegulatorStatus } from '../components/regulator/RegulatorStatus';
import { FrameMonitor } from '../components/protocol/FrameMonitor';
import { useIntersectionState } from '../hooks/useIntersectionState';
import { useTrafficFlow } from '../hooks/useTrafficFlow';
import intersection from '../data/arago-pg-gracia';
import { RegulatorState, StateChangeOrigin } from '../types/protocol';

const DEFAULT_PHASES = [
  { id: 'A', duration: 300 },
  { id: 'B', duration: 50 },
  { id: 'C', duration: 300 },
  { id: 'D', duration: 50 },
];

export function MapIntersectionView() {
  const [showDetectors, setShowDetectors] = useState(false);
  const trafficData = useTrafficFlow();
  const {
    groupColors,
    detectorStates,
    currentPhase,
    cyclePosition,
    cycleTime,
    isRunning,
  } = useIntersectionState(intersection);

  return (
    <div className="relative w-full h-full">
      {/* Map */}
      <div className="absolute inset-0 map-container">
        <MapView
          center={intersection.center}
          zoom={intersection.zoom}
          bearing={intersection.bearing}
          pitch={intersection.pitch}
        >
          <IntersectionLayers geometry={intersection.geometry} />
          <IntersectionMarkers
            groups={intersection.groups}
            groupColors={groupColors}
          />
          {showDetectors && intersection.detectors.map((detector, index) => (
            <DetectorMarker
              key={detector.id}
              detector={detector}
              active={detectorStates[index]}
            />
          ))}
          <CabinetMarker
            cabinet={intersection.cabinet}
            state={RegulatorState.REMOTE_CONTROL}
            isConnected={isRunning}
            currentPlan={3}
            currentPhase={currentPhase}
            cyclePosition={cyclePosition}
            cycleTime={cycleTime}
            groupCount={intersection.groups.length}
            detectorCount={intersection.detectors.length}
            trafficData={trafficData}
          />
        </MapView>
      </div>

      {/* Floating panels */}
      <FloatingPanel
        title="Regulador"
        position="top-left"
        icon={<Radio className="w-3 h-3 text-green-400" />}
        className="w-64"
      >
        <div className="p-2">
          <RegulatorStatus
            state={RegulatorState.REMOTE_CONTROL}
            stateOrigin={StateChangeOrigin.EXTERNAL}
            logicalId={1001}
            physicalId="H0-C1-R1-S0"
            currentPlan={3}
            currentPhase={currentPhase}
            cycleTime={cycleTime}
            cyclePosition={cyclePosition}
            isConnected={isRunning}
            lastUpdate={new Date()}
          />
        </div>
      </FloatingPanel>

      <FloatingPanel
        title="Tramas"
        position="top-right"
        defaultCollapsed={true}
        icon={<Terminal className="w-3 h-3 text-cyan-400" />}
        className="w-80"
      >
        <div className="h-64">
          <FrameMonitor maxFrames={20} showControls={false} />
        </div>
      </FloatingPanel>

      {/* Botón detectores — encima del botón Satélite/Mapa (bottom-24 left-3) */}
      <button
        onClick={() => setShowDetectors(v => !v)}
        className={`absolute bottom-32 left-3 z-50 flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium shadow-md transition-colors cursor-pointer ${
          showDetectors
            ? 'bg-cyan-500/90 hover:bg-cyan-500 text-white'
            : 'bg-white/90 hover:bg-white text-gray-700'
        }`}
        title="Mostrar/ocultar detectores"
      >
        <Radar className="w-3.5 h-3.5" />
        Detectores
      </button>

      <FloatingPanel
        title="Grups"
        position="bottom-right"
        defaultCollapsed={true}
        icon={<LayoutGrid className="w-3 h-3 text-emerald-400" />}
        className="w-auto"
      >
        <MapGroupsDiagram
          groups={intersection.groups}
          phases={DEFAULT_PHASES}
          currentPhase={currentPhase}
        />
      </FloatingPanel>

      <FloatingPanel
        title="Ciclo"
        position="bottom-center"
      >
        <PhaseTimeline
          phases={DEFAULT_PHASES}
          currentPhase={currentPhase}
          cyclePosition={cyclePosition}
          cycleTime={cycleTime}
        />
      </FloatingPanel>
    </div>
  );
}
